import {
  Body,
  Caption,
  Card,
  VBadge,
  VButton,
  VTooltip,
  VTooltipContent,
  VTooltipTrigger,
  cn,
} from '@vrzl/ui';
import { ArrowRight } from 'lucide-react';
import { Fragment, useId, useState } from 'react';

import type { ValidationAnswer, ValidationReportItem } from '@/domains/report/api/types';

import {
  statusBadgeColor,
  statusDescriptions,
  statusIcon,
  statusLabels,
  toggleButtonClass,
} from '../constants';
import { formatValue } from '../utils/formatValue';
import type { ResolvedBlock, ResolvedCriterion } from '../utils/resolveBlock';
import { answerSources } from '../utils/sources';
import { statusNotes } from '../utils/statusNotes';
import { hasHiddenValues, isValueHidden, toggleLabel } from '../utils/visibleValues';

import { ClampedText } from './clampedText';
import { SourcedValue } from './sourcedValue';

function ValueView({ value, type }: { value: string; type: string | null }) {
  const formatted = formatValue(value, type);
  switch (formatted.kind) {
    case 'metric':
      return (
        // Inline i.p.v. flex, zodat line-clamp ook een lange eenheid afkapt.
        <ClampedText>
          <span className="text-3xl font-bold leading-tight text-foreground">{formatted.amount}</span>
          {formatted.unit && (
            <Body as="span" tone="muted" className="ml-0.5">
              {' '}
              {formatted.unit}
            </Body>
          )}
        </ClampedText>
      );
    case 'datetime':
      return (
        <div>
          <Body weight="semibold">{formatted.date}</Body>
          <p className="text-3xl font-bold leading-tight text-foreground">{formatted.time}</p>
        </div>
      );
    case 'text':
      return (
        <ClampedText>
          <Body as="span" weight="semibold" className="text-base">
            {formatted.text}
          </Body>
        </ClampedText>
      );
  }
}

function AnswerValue({
  answer,
  documents,
  eventId,
  criterionId,
  type,
}: {
  answer: ValidationAnswer;
  documents: ValidationReportItem['documents'];
  eventId: string;
  criterionId: string;
  type: string | null;
}) {
  return (
    <SourcedValue
      eventId={eventId}
      criterionId={criterionId}
      sources={answerSources(answer, documents)}
      highlightQuery={answer.value ?? undefined}
    >
      {answer.value === null ? (
        <Body tone="muted" className="italic">
          Niet aanwezig
        </Body>
      ) : (
        <ValueView value={answer.value} type={type} />
      )}
    </SourcedValue>
  );
}

function CriterionSection({
  criterion,
  eventId,
  showName,
}: {
  criterion: ResolvedCriterion;
  eventId: string;
  showName: boolean;
}) {
  const { item, type } = criterion;
  const [onlyAnswer] = item.answers;
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {showName && <Caption>{item.name}</Caption>}

      {item.answers.length === 0 ? (
        <Body tone="muted" className="italic">
          Niet aanwezig
        </Body>
      ) : item.answers.length === 1 && onlyAnswer ? (
        <AnswerValue
          answer={onlyAnswer}
          documents={item.documents}
          eventId={eventId}
          criterionId={item.id}
          type={type}
        />
      ) : (
        // Meerdere gevonden waarden: onder elkaar, elk met eigen bronnen. Ingeklapt blijven de
        // overige waarden in de DOM maar verborgen; op papier staan ze er altijd.
        // Lijn boven elke waarde i.p.v. divide-y: dat kijkt naar :last-child in de DOM en zou
        // ingeklapt een extra lijn onder de laatste zichtbare waarde zetten.
        <div className="flex flex-col gap-1">
          <ul id={listId}>
            {item.answers.map((answer, i) => (
              <li
                key={i}
                className={cn(
                  'border-t border-border py-2',
                  isValueHidden(i, expanded) && 'hidden print:block',
                )}
              >
                <AnswerValue
                  answer={answer}
                  documents={item.documents}
                  eventId={eventId}
                  criterionId={item.id}
                  type={null}
                />
              </li>
            ))}
          </ul>
          {hasHiddenValues(item.answers.length) && (
            <VButton
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={expanded}
              aria-controls={listId}
              className={cn('self-start', toggleButtonClass)}
              onClick={() => setExpanded((v) => !v)}
            >
              {toggleLabel(item.answers.length, expanded)}
            </VButton>
          )}
        </div>
      )}
    </div>
  );
}

interface OverviewBlockCardProps {
  block: ResolvedBlock;
  eventId: string;
}

export function OverviewBlockCard({ block, eventId }: OverviewBlockCardProps) {
  const StatusIcon = statusIcon[block.status];
  const showNames = block.criteria.length > 1;
  const notes = statusNotes(block.criteria.map((c) => c.item));

  return (
      <Card padding="md" className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <Body as="span" tone="muted" className="min-w-0 flex-1">
            {block.title}
          </Body>
          <VTooltip>
            <VTooltipTrigger asChild>
              <VBadge
                color={statusBadgeColor[block.status]}
                shape="pill"
                tabIndex={0}
                role="img"
                aria-label={statusLabels[block.status]}
                className="size-6 shrink-0 cursor-help justify-center p-0 outline-none focus-visible:ring-2 focus-visible:ring-neutral-500"
              >
                <StatusIcon className="size-3.5" aria-hidden />
              </VBadge>
            </VTooltipTrigger>
            <VTooltipContent>
              <Caption className="font-medium text-foreground">{statusLabels[block.status]}</Caption>
              <Caption>{statusDescriptions[block.status]}</Caption>
              {notes.map((note) => (
                <Caption key={note}>{note}</Caption>
              ))}
            </VTooltipContent>
          </VTooltip>
        </div>

        <div className={cn('flex gap-4', block.isRange ? 'flex-row items-center' : 'flex-col')}>
          {block.criteria.map((criterion, i) => (
            <Fragment key={criterion.item.id}>
              {block.isRange && i === 1 && (
                <ArrowRight className="size-5 shrink-0 text-muted-foreground" aria-hidden />
              )}
              <CriterionSection criterion={criterion} eventId={eventId} showName={showNames} />
            </Fragment>
          ))}
        </div>
      </Card>
  );
}
